import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, X, Zap } from 'lucide-react'

/**
 * SubscriptionBanner - Shows a warning when subscription is expiring, expired, or past due
 * 
 * Props:
 * - subscriptionStatus: { status: string, daysRemaining: number | null }
 * - isPro: boolean
 * - onRenew: function (optional callback to trigger renewal modal directly)
 */
export default function SubscriptionBanner({ subscriptionStatus, isPro, onRenew }) {
    const [isDismissed, setIsDismissed] = useState(false)

    useEffect(() => {
        // Check if banner was dismissed within last 24 hours
        const dismissedAt = localStorage.getItem('inkspot_subscription_banner_dismissed')
        if (dismissedAt) {
            const dismissedTime = new Date(dismissedAt)
            const now = new Date()
            const hoursSinceDismiss = (now - dismissedTime) / (1000 * 60 * 60)

            // Hide for 24 hours after dismissal
            if (hoursSinceDismiss < 24) {
                setIsDismissed(true)
            } else {
                // Clear old dismissal
                localStorage.removeItem('inkspot_subscription_banner_dismissed')
            }
        }
    }, [])

    const handleDismiss = () => {
        localStorage.setItem('inkspot_subscription_banner_dismissed', new Date().toISOString())
        setIsDismissed(true)
    }

    // Show for: expiring (Pro), expired, or past_due statuses
    const showableStatuses = ['expiring', 'expired', 'past_due']
    if (!showableStatuses.includes(subscriptionStatus.status) || isDismissed) {
        return null
    }

    // For 'expiring', only show if the user is still Pro
    if (subscriptionStatus.status === 'expiring' && !isPro) {
        return null
    }

    const daysRemaining = subscriptionStatus.daysRemaining || 0
    const status = subscriptionStatus.status

    // Determine banner style based on status
    const isExpired = status === 'expired'
    const isPastDue = status === 'past_due'
    const isUrgent = isExpired || isPastDue || daysRemaining <= 3

    const getBannerColors = () => {
        if (isExpired) return 'bg-red-500/10 border-red-500/30'
        if (isPastDue) return 'bg-orange-500/10 border-orange-500/30'
        if (isUrgent) return 'bg-red-500/10 border-red-500/30'
        return 'bg-yellow-500/10 border-yellow-500/30'
    }

    const getTextColor = () => {
        if (isExpired) return 'text-red-400'
        if (isPastDue) return 'text-orange-400'
        if (isUrgent) return 'text-red-400'
        return 'text-yellow-400'
    }

    const getSubTextColor = () => {
        if (isExpired) return 'text-red-400/70'
        if (isPastDue) return 'text-orange-400/70'
        if (isUrgent) return 'text-red-400/70'
        return 'text-yellow-400/70'
    }

    const getTitle = () => {
        if (isExpired) return 'Subscription Expired'
        if (isPastDue) return 'Payment Failed'
        if (isUrgent) return 'Subscription Expiring Soon!'
        return 'Subscription Expiring'
    }

    const getMessage = () => {
        if (isExpired) return 'Your Pro subscription has expired. Resubscribe to regain access to Pro features.'
        if (isPastDue) return 'Your last payment failed. Update your payment method to keep Pro access.'
        return `Your Pro access will end in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.${isUrgent ? ' Renew now to keep your Pro features.' : ''}`
    }

    const getButtonStyle = () => {
        if (isExpired) return 'bg-red-500 hover:bg-red-600 text-white'
        if (isPastDue) return 'bg-orange-500 hover:bg-orange-600 text-white'
        if (isUrgent) return 'bg-red-500 hover:bg-red-600 text-white'
        return 'bg-yellow-500 hover:bg-yellow-600 text-slate-900'
    }

    const getDismissStyle = () => {
        if (isExpired) return 'text-red-400/50 hover:text-red-400 hover:bg-red-500/10'
        if (isPastDue) return 'text-orange-400/50 hover:text-orange-400 hover:bg-orange-500/10'
        if (isUrgent) return 'text-red-400/50 hover:text-red-400 hover:bg-red-500/10'
        return 'text-yellow-400/50 hover:text-yellow-400 hover:bg-yellow-500/10'
    }

    return (
        <div className={`mb-6 p-4 rounded-xl border flex items-start sm:items-center justify-between gap-4 ${getBannerColors()}`}>
            <div className="flex items-start sm:items-center gap-3">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${getTextColor()}`} />
                <div>
                    <p className={`font-medium ${getTextColor()}`}>
                        {getTitle()}
                    </p>
                    <p className={`text-sm ${getSubTextColor()}`}>
                        {getMessage()}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
                {onRenew ? (
                    <button
                        onClick={onRenew}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${getButtonStyle()}`}
                    >
                        <Zap className="w-4 h-4" />
                        {isExpired ? 'Resubscribe' : isPastDue ? 'Update Payment' : 'Renew Now'}
                    </button>
                ) : (
                    <Link
                        to="/#pricing"
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${getButtonStyle()}`}
                    >
                        <Zap className="w-4 h-4" />
                        {isExpired ? 'Resubscribe' : isPastDue ? 'Update Payment' : 'Renew Now'}
                    </Link>
                )}
                <button
                    onClick={handleDismiss}
                    className={`p-2 rounded-lg transition-colors ${getDismissStyle()}`}
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

